import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHotel } from "../contexts/HotelContext";
import { getBrandKit, updateBrandKit, uploadHeroImage } from "../services/api";
import Layout from "../components/Layout";
import { SpinnerIcon } from "../components/icons";
import PublicOfferPreview from "../components/PublicOfferPreview";
import { mockOfferData } from "../components/branded-offer/MockOfferData";
import { TemplateId } from "../components/offer-templates/BaseTemplate";
import { useAutoSave } from "../hooks/useAutoSave";

interface BrandKitData {
  brandKit: {
    keyVisualUrl?: string;
    templateId?: TemplateId;
    colors?: {
      primary?: string;
      secondary?: string;
      accent?: string;
      gradientStart?: string;
      gradientEnd?: string;
      hotelInfo?: string;
      dayNumber?: string;
      dayBackground?: string;
    };
  };
  offerSections: {
    termsAndConditions?: {
      enabled: boolean;
      content?: string;
      position?: "before" | "after";
    };
  };
}

const BrandKitDesign: React.FC = () => {
  const { user } = useHotel();
  const hotelId = user?.hotels?.[0]?.id;

  const [brandKitData, setBrandKitData] = useState<BrandKitData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);
  const [uploadingHero, setUploadingHero] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Debounced save functions for colors
  const saveColors = useCallback(
    async (colors: any) => {
      if (!hotelId) return;
      await updateBrandKit(hotelId, {
        brandKit: {
          ...brandKitData?.brandKit,
          colors,
        },
      });
    },
    [hotelId, brandKitData?.brandKit]
  );

  const {
    value: debouncedColors,
    updateValue: updateColorsDebounced,
    isSaving: isSavingColors,
  } = useAutoSave(brandKitData?.brandKit?.colors || {}, {
    delay: 500,
    onSave: saveColors,
    onError: (error) => console.error("Error updating colors:", error),
  });

  // Debounced save functions for terms and conditions
  const saveTermsAndConditions = useCallback(
    async (termsAndConditions: any) => {
      if (!hotelId) return;
      await updateBrandKit(hotelId, {
        offerSections: {
          ...brandKitData?.offerSections,
          termsAndConditions,
        },
      });
    },
    [hotelId, brandKitData?.offerSections]
  );

  const {
    updateValue: updateTermsDebounced,
    isSaving: isSavingTerms,
  } = useAutoSave(
    brandKitData?.offerSections?.termsAndConditions || { enabled: false },
    {
      delay: 800,
      onSave: saveTermsAndConditions,
      onError: (error) =>
        console.error("Error updating terms and conditions:", error),
    }
  );

  useEffect(() => {
    const fetchBrandKit = async () => {
      if (!hotelId) return;

      try {
        setLoading(true);
        const data = await getBrandKit(hotelId);
        setBrandKitData(data);
      } catch (error) {
        console.error("Error fetching brand kit:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBrandKit();
  }, [hotelId]);

  const updateColors = (colorType: string, color: string) => {
    if (!hotelId || !brandKitData) return;

    const updatedColors = {
      ...debouncedColors,
      [colorType]: color,
    };

    // Update local state immediately for instant UI feedback
    setBrandKitData({
      ...brandKitData,
      brandKit: {
        ...brandKitData.brandKit,
        colors: updatedColors,
      },
    });

    // Update debounced colors (will trigger API call after delay)
    updateColorsDebounced(updatedColors);
    // Don't hide color picker - let user continue editing or click elsewhere to close
  };

  const handleHeroUpload = async (file: File) => {
    if (!hotelId) return;

    try {
      setUploadingHero(true);
      const response = await uploadHeroImage(hotelId, file);

      const updatedData = {
        ...brandKitData!,
        brandKit: {
          ...brandKitData!.brandKit,
          keyVisualUrl: response.imageUrl,
        },
      };

      setBrandKitData(updatedData);
    } catch (error) {
      console.error("Error uploading hero image:", error);
    } finally {
      setUploadingHero(false);
    }
  };

  const handleRemoveHero = async () => {
    if (!hotelId || !brandKitData) return;

    const updatedData = {
      ...brandKitData,
      brandKit: {
        ...brandKitData.brandKit,
        keyVisualUrl: undefined,
      },
    };

    try {
      await updateBrandKit(hotelId, { brandKit: updatedData.brandKit });
      setBrandKitData(updatedData);
    } catch (error) {
      console.error("Error removing hero image:", error);
    }
  };

  const updateTermsAndConditions = (enabled: boolean, content?: string) => {
    if (!hotelId) return;

    const updatedTerms = {
      enabled,
      content: content || "",
      position: "after" as const,
    };

    // Update local state immediately for instant UI feedback
    setBrandKitData({
      ...brandKitData!,
      offerSections: {
        ...brandKitData!.offerSections,
        termsAndConditions: updatedTerms,
      },
    });

    // Update debounced terms (will trigger API call after delay)
    updateTermsDebounced(updatedTerms);
  };


  if (!user || loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <SpinnerIcon className="h-12 w-12 text-primary-600 mx-auto mb-4" />
          <p className="text-lg text-neutral-600">
            Ładowanie edytora brandingu...
          </p>
        </div>
      </div>
    );
  }

  const displayColors = brandKitData?.brandKit?.colors || {};
  const templateId = brandKitData?.brandKit?.templateId || "modern";
  const displayTermsAndConditions =
    brandKitData?.offerSections?.termsAndConditions;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6">
        {/* Header with Edit Mode Toggle */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">
              Design oferty
            </h1>
            <p className="text-neutral-600 mt-1">
              Dostosuj wygląd swoich ofert publicznych
            </p>
            {(isSavingColors || isSavingTerms) && (
              <div className="flex items-center mt-2 text-sm text-primary-700">
                <SpinnerIcon className="h-4 w-4 mr-1" />
                Zapisywanie...
              </div>
            )}
          </div>

          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={editMode}
              onChange={(e) => setEditMode(e.target.checked)}
              className="sr-only"
            />
            <div
              className={`relative inline-block w-14 h-8 transition-colors duration-200 ease-in-out rounded-full ${
                editMode ? "bg-primary-700" : "bg-neutral-300"
              }`}
            >
              <div
                className={`absolute left-1 top-1 w-6 h-6 bg-white rounded-full transition-transform duration-200 ease-in-out ${
                  editMode ? "translate-x-6" : "translate-x-0"
                }`}
              />
            </div>
            <span className="ml-3 text-sm font-medium text-neutral-700">
              Tryb edycji
            </span>
          </label>
        </div>

        {/* Preview Container */}
        <div
          className="bg-white rounded-lg border border-neutral-200 overflow-hidden"
          style={
            {
              "--primary-color": displayColors.primary || "#3B82F6",
              "--secondary-color": displayColors.secondary || "#6B7280",
              "--accent-color": displayColors.accent || "#10B981",
              "--gradient-start": displayColors.gradientStart || "#334155",
              "--gradient-end": displayColors.gradientEnd || "#1e293b",
            } as React.CSSProperties
          }
        >
          {/* Complete PublicOffer Preview */}
          <div className="p-6">
            <PublicOfferPreview
              offer={mockOfferData}
              brandKit={brandKitData?.brandKit}
              termsAndConditions={displayTermsAndConditions}
              templateId={templateId}
              editMode={editMode}
              onColorChange={updateColors}
              showColorPicker={showColorPicker}
              setShowColorPicker={setShowColorPicker}
              onHeroUpload={handleHeroUpload}
              onHeroRemove={handleRemoveHero}
              uploadingHero={uploadingHero}
              onTermsToggle={(enabled: boolean) =>
                updateTermsAndConditions(enabled)
              }
              onTermsContentChange={(content: string) =>
                updateTermsAndConditions(true, content)
              }
            />
          </div>
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleHeroUpload(file);
          }}
          className="hidden"
        />
      </div>
    </Layout>
  );
};

export default BrandKitDesign;
